/**
 * Veeso Project API definitions
 */

import { defHTTP } from '../../api-builder';
import type {
  Project,
  GetProjectsParams,
  GetRecentProjectsParams,
  GetTrashedProjectsParams,
  CreateProjectRequest,
  UpdateProjectRequest,
  StoreProjectRequest,
  StoreProjectResponse,
  FinalizeProjectRequest,
  FinalizeProjectResponse,
  UpdateProjectThumbnailRequest,
  GetProjectParams,
  DeleteProjectParams,
  ProjectInteractionRequest,
  ProjectInteractionResponse,
  Template,
  CreateTemplateRequest,
  GetTemplatesByTagParams,
} from './types';

/**
 * Project API definitions for use with apiBuilder
 */
export const projectAPIDefinitions = {
  // ============================================================================
  // Project CRUD
  // ============================================================================

  /**
   * Create a new project
   */
  createProject: defHTTP<CreateProjectRequest, Project>('POST', '/project'),

  /**
   * Get a single project by ID
   * Note: password is passed as query param for private projects
   */
  getProject: defHTTP<GetProjectParams, Project>('GET', '/project/:id'),

  /**
   * Update a project by ID
   */
  updateProject: defHTTP<{ id: string } & UpdateProjectRequest, Project>('PUT', '/project/:id'),

  /**
   * Store project content (get upload URL)
   */
  storeProject: defHTTP<{ id: string } & StoreProjectRequest, StoreProjectResponse>(
    'PUT',
    '/project/:id/store'
  ),

  /**
   * Finalize project update
   */
  finalizeProject: defHTTP<{ id: string } & FinalizeProjectRequest, FinalizeProjectResponse>(
    'POST',
    '/project/:id/finalize'
  ),

  /**
   * Update project thumbnail (FormData upload)
   */
  updateProjectThumbnail: defHTTP<UpdateProjectThumbnailRequest, Project>(
    'PUT',
    '/project/:id/thumbnail',
    { bodySerializer: 'formData' }
  ),

  /**
   * Delete a project (move to trash)
   */
  deleteProject: defHTTP<DeleteProjectParams, { success: boolean }>('DELETE', '/project/:id'),

  /**
   * Permanently delete a project (confirmed deletion)
   */
  deleteProjectConfirmed: defHTTP<DeleteProjectParams, { success: boolean }>(
    'DELETE',
    '/project/:id/confirmed'
  ),

  /**
   * Restore a project from trash
   */
  restoreProject: defHTTP<{ id: string }, Project>('PUT', '/project/:id/restore'),

  // ============================================================================
  // Project Lists
  // ============================================================================

  /**
   * Get list of projects with pagination, sorting, and filtering
   */
  getProjects: defHTTP<GetProjectsParams, Project[]>('GET', '/project'),

  /**
   * Get all projects (with filter type)
   */
  getAllProjects: defHTTP<GetProjectsParams, Project[]>('GET', '/project/_/all'),

  /**
   * Get recent projects
   */
  getRecentProjects: defHTTP<GetRecentProjectsParams, Project[]>('GET', '/project/recent'),

  /**
   * Get featured projects
   */
  getFeaturedProjects: defHTTP<void, Project[]>('GET', '/project/featured'),

  /**
   * Get trashed projects
   */
  getTrashedProjects: defHTTP<GetTrashedProjectsParams, Project[]>('GET', '/project/trash'),

  // ============================================================================
  // Project Interactions
  // ============================================================================

  /**
   * Record a project interaction (view, edit, share)
   */
  createProjectInteraction: defHTTP<ProjectInteractionRequest, ProjectInteractionResponse>(
    'POST',
    '/api/project-interaction'
  ),

  // ============================================================================
  // Templates
  // ============================================================================

  /**
   * Create a new template
   */
  createTemplate: defHTTP<CreateTemplateRequest, Template>('POST', '/template'),

  /**
   * Get a template by ID
   */
  getTemplate: defHTTP<{ id: string }, Template>('GET', '/template/:id'),

  /**
   * Get shared templates
   */
  getSharedTemplates: defHTTP<void, Template[]>('GET', '/shared/template'),

  /**
   * Get templates by tag
   */
  getTemplatesByTag: defHTTP<GetTemplatesByTagParams, Template[]>(
    'GET',
    '/shared/template/tag/:tag'
  ),
};
